import { Field as FinalField } from 'react-final-form';
import type { FieldState } from 'final-form';
import Field from '@lendi-ui/field';
import TextArea, { TextAreaProps } from '@lendi-ui/text-area';

import { getAriaLabelById, validateFixedLength, validateNotEmptyString } from '@funnels/utils';

import { FieldLabel } from '../_common/FieldLabel';
import { FieldWrapper } from '../_common/style';
import { CommonFieldProps } from '../_common/types';

interface FieldProps {
  name: string;
  format?: (value: any) => any;
  parse?: (value: any) => any;
  validate?: (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => any;
}
export interface TextAreaFieldProps extends CommonFieldProps, FieldProps, Omit<TextAreaProps, 'name' | 'onChange'> {
  fixedLength?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical' | 'block' | 'inline';
}

const TextAreaField = (props: TextAreaFieldProps) => {
  const {
    label,
    name,
    subLabel,
    isDisabled,
    isOptional,
    validationFailMessage,
    parse,
    format,
    validate,
    fixedLength,
    disableFailMessage = false,
    maxRows,
    rows,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    piiStringInLabel,
    resize = 'none',
  } = props;
  const fieldProps: FieldProps = {
    name,
    parse,
    format,
  };

  const inputProps = {
    isDisabled,
    maxRows,
    rows,
  };

  if (!isDisabled) {
    if (validate) {
      fieldProps.validate = validate(validationFailMessage);
    } else if (fixedLength) {
      fieldProps.validate = validateFixedLength(validationFailMessage, fixedLength);
    } else {
      fieldProps.validate = validateNotEmptyString(validationFailMessage, isOptional);
    }
  }

  /**
   * https://final-form.org/docs/react-final-form/types/FieldProps
   * Please read <validate> part of this doc.
   * Current implementation modifies <validate> function if some props changed.
   * The <validate> modification will not be applied immediately, which means it will be applied when you change values of other fields.
   * Props changes outside will not cause unmount, so the <validate> modification is delayed.
   * Therefore, we need to set dynamic <key> for <Field> to force it totally destroy and re-register to form
   */
  return (
    <FieldWrapper>
      <FieldLabel
        label={label}
        subLabel={subLabel}
        idPostfix={name}
        piiStringInLabel={piiStringInLabel}
        isOptional={isOptional}
      />
      <FinalField {...fieldProps} key={`${name}-${isOptional}-${isDisabled}-${fixedLength}`}>
        {({ input, meta }) => (
          <Field {...meta} error={disableFailMessage ? undefined : meta.error}>
            <TextArea
              size="md"
              {...input}
              {...inputProps}
              isFullWidth
              isError={!!meta.error && !!meta.touched}
              aria-labelledby={getAriaLabelById('label', label ? name : undefined, ariaLabelledby)}
              aria-describedby={getAriaLabelById('description', subLabel ? name : undefined, ariaDescribedby)}
              aria-invalid={disableFailMessage ? false : !!meta.error}
              style={{ resize }}
            />
          </Field>
        )}
      </FinalField>
    </FieldWrapper>
  );
};

export default TextAreaField;
